import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { authGuard } from '../guards/auth.guard';
import { CanDeactivateGuard } from '../guards/can-deactivate.guard';
import { LoginComponent } from '../pages/auth/login/login.component';
import { DashboardComponent } from '../pages/dashboards/dashboard/dashboard.component';
import { EpdDashboardComponent } from '../pages/dashboards/epd-dashboard/epd-dashboard.component';
import { Scope3Component } from '../pages/dashboards/scope-3/scope-3.component';
import { EcodesignComponent } from '../pages/ecodesign/ecodesign.component';
import { RequestComponent } from '../pages/information-requests/request/request.component';
import { SupplierDashboardComponent } from '../pages/information-requests/supplier-dashboard/supplier-dashboard.component';
import { LayersComponent } from '../pages/mattermaps/components/layers/layers.component';
import { MattermapsEditComponent } from '../pages/mattermaps/components/mattermaps-edit/mattermaps-edit.component';
import { MattermapsComponent } from '../pages/mattermaps/mattermaps.component';
import { PefAnalysisComponent } from '../pages/pef-analysis/pef-analysis.component';
import { ReportDetailComponent } from '../pages/report/report-detail/report-detail.component';
import { ReportLandingComponent } from '../pages/report/report-landing/report-landing.component';
import { LayoutComponent } from './layout.component';

const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent
  },
  {
    path: 'resetPassword',
    loadComponent: () =>
      import('../pages/auth/reset-password/reset-password.component').then((m) => m.ResetPasswordComponent)
  },
  {
    path: 'invited',
    loadComponent: () => import('../pages/auth/invite/invite.component').then((m) => m.InviteComponent)
  },
  {
    path: 'error',
    loadComponent: () =>
      import('../pages/auth/session-expired/session-expired.component').then((m) => m.SessionExpiredComponent)
  },
  {
    path: '',
    component: LayoutComponent,
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'scope-3',
        component: Scope3Component
      },
      {
        path: 'dashboard',
        component: DashboardComponent,
        data: { clearSearch: true }
      },
      {
        path: 'mattermaps/:layer/:rid',
        component: MattermapsComponent,
        children: [
          { path: '', component: LayersComponent },
          { path: 'edit', component: MattermapsEditComponent, canDeactivate: [CanDeactivateGuard] }
        ]
      },
      {
        path: 'supplier-breakdown/:id',
        loadComponent: () =>
          import('../pages/dashboards/supplier-breakdown/supplier-breakdown.component').then(
            (m) => m.SupplierBreakdownComponent
          )
      },
      {
        path: 'importer/:name',
        loadComponent: () => import('../pages/importers/importer/importer.component').then((m) => m.ImporterComponent),
        canDeactivate: [CanDeactivateGuard]
      },
      {
        path: 'product-overview/:rid',
        loadComponent: () =>
          import('../pages/product-overview/components/product-overview.component').then(
            (m) => m.ProductOverviewComponent
          )
      },
      {
        path: 'advance-importer',
        loadComponent: () =>
          import('../pages/importers/advance-importer-list/advance-importer-list.component').then(
            (m) => m.AdvanceImporterListComponent
          )
      },
      {
        path: 'report',
        loadComponent: () => import('../pages/report/report.component').then((m) => m.ReportComponent),
        children: [
          { path: '', component: ReportLandingComponent },
          {
            path: 'advanced-views',
            children: [
              {
                path: '**',
                loadComponent: () => import('../pages/iframe/iframe.component').then((m) => m.IframeComponent)
              }
            ]
          },
          { path: ':rid', component: ReportDetailComponent }
        ]
      },
      {
        path: 'advance-importer/:name',
        loadComponent: () =>
          import('../pages/importers/advance-importer/advance-importer.component').then(
            (m) => m.AdvanceImporterComponent
          )
      },
      {
        path: 'profile/:rid',
        loadComponent: () =>
          import('../pages/profile/components/profile/profile.component').then((m) => m.ProfileComponent)
      },
      {
        path: 'jobs',
        data: { hideScroll: true },
        loadComponent: () => import('../pages/jobs/jobs.component').then((m) => m.JobsComponent)
      },
      {
        path: 'lookup-tool',
        loadComponent: () => import('../pages/lookup/lookup.component').then((m) => m.LookUpComponent)
      },
      {
        path: 'manage/admin',
        loadComponent: () => import('../pages/admin-panel-v2/admin-panel.component').then((m) => m.AdminPanelComponent)
      },
      {
        path: 'information-requests',
        loadComponent: () =>
          import('../pages/information-requests/information-requests.component').then(
            (m) => m.InformationRequestsComponent
          ),
        children: [
          { path: 'supplier', component: SupplierDashboardComponent },
          { path: 'request/:rid', component: RequestComponent, canDeactivate: [CanDeactivateGuard] }
        ]
      },
      {
        path: 'data-collection',
        loadChildren: () =>
          import('../pages/data-collection/data-collection.module').then((m) => m.DataCollectionModule)
      },
      {
        path: 'pef-analysis/:rid',
        component: PefAnalysisComponent
      },
      {
        path: 'mcda/:rid',
        loadComponent: () => import('../pages/mcda/mcda.component').then((m) => m.McdaComponent)
      },
      {
        path: 'ecodesign/:rid',
        component: EcodesignComponent
      },
      {
        path: 'ecodesign-builder',
        loadComponent: () =>
          import('../pages/dashboards/ecodesign-builder/ecodesign-builder.component').then(
            (m) => m.EcodesignBuilderComponent
          ),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('../pages/dashboards/ecodesign-builder/components/ecodesign-blueprint-list/ecodesign-blueprint-list.component').then(
                (m) => m.EcodesignBlueprintListComponent
              )
          },
          {
            path: ':rid',
            loadComponent: () =>
              import('../pages/dashboards/ecodesign-builder/components/ecodesign-overview/ecodesign-overview.component').then(
                (m) => m.EcodesignOverviewComponent
              )
          }
        ]
      },
      {
        path: 'configurator',
        loadChildren: () => import('../pages/configurator/configurator.module').then((m) => m.ConfiguratorModule)
      },
      {
        path: 'scip/:rid',
        loadChildren: () =>
          import('../pages/scip-integration/scip-integration.module').then((m) => m.ScipIntegrationModule)
      },
      {
        path: 'my-site',
        loadComponent: () => import('../pages/my-drive/my-drive.component').then((m) => m.MyDriveComponent)
      },
      {
        path: 'my-site-old',
        loadComponent: () => import('../pages/my-site/my-site.component').then((m) => m.MySiteComponent)
      },
      {
        path: 'data-management',
        loadChildren: () =>
          import('../pages/master-data-management/master-data-management.module').then(
            (m) => m.MasterDataManagementModule
          )
      },
      {
        path: 'cbam-dashboard/:rid',
        loadChildren: () => import('../pages/cbam/cbam.module').then((m) => m.CbamModule)
      },
      {
        path: 'trash',
        loadComponent: () => import('../pages/trash/trash.component').then((m) => m.TrashComponent)
      },
      { path: 'epd-dashboard/:rid', component: EpdDashboardComponent },
      {
        path: 'to-dos',
        data: { hideScroll: true },
        loadComponent: () => import('../pages/to-do/components/to-dos/to-dos.component').then((m) => m.ToDosComponent)
      },
      {
        path: 'data-explorer',
        loadComponent: () =>
          import('../pages/data-explorer/data-explorer.component').then((m) => m.DataExplorerComponent)
      },
      {
        path: 'data-statistics',
        loadComponent: () =>
          import('../pages/data-statistics/data-statistics.component').then((m) => m.DataStatisticsComponent)
      },
      {
        path: 'billing',
        loadComponent: () => import('../pages/billing/billing.component').then((m) => m.BillingComponent)
      },
      {
        path: 'compliance-audit',
        loadComponent: () =>
          import('../pages/compliance-audit/compliance-audit.component').then((m) => m.ComplianceAuditComponent)
      },
      {
        path: 'data/sources',
        loadComponent: () => import('../pages/data-source/data-source.component').then((m) => m.DataSourceComponent)
      },
      {
        path: 'reports/history',
        loadComponent: () =>
          import('../pages/reports-history/reports-history.component').then((m) => m.ReportsHistoryComponent)
      },
      {
        path: 'explorer/:rid',
        loadComponent: () => import('../pages/bom-explorer/bom-explorer.component').then((m) => m.BomExplorerComponent)
      },
      {
        path: 'taxonomies',
        loadComponent: () => import('../pages/taxonomies/taxonomies.component').then((m) => m.TaxonomiesComponent)
      },
      {
        path: 'epd-management',
        loadComponent: () =>
          import('../pages/epd-management/epd-management.component').then((m) => m.EPDManagementComponent)
      },
      {
        path: 'commodities',
        loadComponent: () => import('../pages/commodities/commodities.component').then((m) => m.CommoditiesComponent)
      },
      {
        path: 'costing-dashboard',
        loadComponent: () => import('../pages/dashboards/costing/costing').then((m) => m.CostingComponent)
      },
      {
        path: 'mario-app',
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: '/dashboard'
          },
          {
            path: ':slug',
            children: [
              {
                path: '**',
                loadComponent: () => import('../pages/iframe/iframe.component').then((m) => m.IframeComponent)
              }
            ]
          }
        ]
      }
    ]
  }
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule]
})
export class LayoutRoutingModule {}
